import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ListItem,
  List,
  ListItemText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  Tooltip,
  TableFooter,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  ArrowDownward,
  ArrowUpward,
  AccountBalance,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  Bed,
  Hotel,
  CreditCard,
  CalendarToday,
  Phone,
  Email,
  NightsStay,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomerPayment,
  getCustomerTransactions,
  searchTransactionsByCode,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import ReceiptModal from '../../../components/sales/ReceiptModal';
import { getBusinessData } from "../../../common/utils";
import ReceiptModal_Large from "../../../components/sales/ReceiptModal_large";
import ReceiptModal_giftcard_crypto from "../../../components/sales/ReceiptModal_giftcard_crypto";
import { MenuButton } from "../../../components/Menu";

// Updated interfaces for hotel booking
interface RoomItem {
  product_id: string;
  product_name: string;
  card_amount: string;
  card_rate: string;
  naira_rate: string;
  rmbValue: string;
  load_type: string;
  quantity?: string;
  price?: string;
  total_amount?: string;
  item_status?: string;
  // Additional hotel fields
  room_type?: string;
  room_number?: string;
  check_in_date?: string;
  check_out_date?: string;
  total_nights?: number;
  adults_count?: number;
  children_count?: number;
  rate_per_night?: string;
}

interface HotelBooking {
  order_id: string;
  order_code: string;
  order_date: string;
  order_status: "CONFIRMED" | "PENDING" | "CANCELLED" | "CHECKED_OUT";
  payment_status: '0' | '1' | '2';
  total_price: string;
  payable_amount: string;
  amount_paid: string;
  balance_due: string;
  member_id: string;
  member_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  mode_of_payment: string;
  order_by: string;
  otp_status: string | null;
  items: RoomItem[];
  processor_type: string;
  external_processor: string;
  customer_account_name: string;
  customer_account_number: string;
  customer_bank_code: string;
  bank_name: string;
  product_name: string;
  product_type: string;
  commission: string;
  costPrice: string;
  sellingPrice: string;
  proof_of_payment: string;
  giftcardType?: string;
  product_face_code?: string;
  cryptoType?: string;
  crypto_value?: string;
  note1?: string;
  note2?: string;
  note3?: string;
  // Additional hotel fields
  check_in_date?: string;
  check_out_date?: string;
  total_nights?: number;
  total_adults?: number;
  total_children?: number;
  special_requests?: string;
  check_in_time?: string;
  check_out_time?: string;
  hotel_name?: string;
  hotel_branch?: string;
  guest_id_type?: string;
  guest_id_number?: string;
  emergency_contact?: string;
  room_service_included?: boolean;
  breakfast_included?: boolean;
  wifi_included?: boolean;
  parking_included?: boolean;
}

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

interface SearchResponse {
  status: boolean;
  message: string;
  data: {
    orders: Array<{
      order: HotelBooking;
      items: Array<{
        order: HotelBooking;
      }>;
    }>;
    balance: number;
  };
}

const HotelBookingSearch: React.FC = () => {
  const location = useLocation<{ customer?: Customer }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedBookings, setSearchedBookings] = useState<HotelBooking[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<HotelBooking | null>(null);
  const [bookings, setBookings] = useState<HotelBooking[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState({
    bookings: false,
    payment: false,
    search: false,
  });

  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  // Helper function to transform API response to HotelBooking format
  const transformApiOrderToHotelBooking = (apiOrder: any): HotelBooking => {
    const orderData = apiOrder.order || apiOrder;
    
    // Create items array from the booking data
    const items: RoomItem[] = [{
      product_id: orderData.product_id_single || orderData.product_id,
      product_name: orderData.product_name,
      card_amount: orderData.card_amount,
      card_rate: orderData.card_rate,
      naira_rate: orderData.naira_rate,
      rmbValue: orderData.rmbValue,
      load_type: orderData.load_type,
      quantity: "1",
      price: orderData.card_amount,
      total_amount: orderData.total_price,
      item_status: orderData.active_status,
      // Hotel specific fields - extract from notes or separate fields
      room_type: orderData.room_type || orderData.product_id_type,
      check_in_date: orderData.check_in_date,
      check_out_date: orderData.check_out_date,
      total_nights: orderData.total_nights,
      adults_count: orderData.total_adults || 1,
      children_count: orderData.total_children || 0,
      rate_per_night: orderData.rate_per_night || orderData.card_rate
    }];

    return {
      order_id: orderData.order_id,
      order_code: orderData.order_code,
      order_date: orderData.order_date,
      order_status: orderData.active_status,
      payment_status: orderData.payment_status?.toString() as '0' | '1' | '2',
      total_price: orderData.total_price,
      payable_amount: orderData.payable_amount,
      amount_paid: orderData.amount_paid,
      balance_due: orderData.balance_due,
      member_id: orderData.member_id,
      member_name: orderData.member_name || orderData.customer_name,
      customer_name: orderData.customer_name,
      customer_email: orderData.customer_email,
      customer_phone: orderData.customer_phone,
      mode_of_payment: orderData.mode_of_payment,
      order_by: orderData.order_by,
      otp_status: orderData.otp_status,
      items: items,
      processor_type: orderData.processor_type,
      external_processor: orderData.external_processor,
      customer_account_name: orderData.customer_account_name,
      customer_account_number: orderData.customer_account_number,
      customer_bank_code: orderData.customer_bank_code,
      bank_name: orderData.bank_name,
      product_name: orderData.product_name,
      product_type: orderData.product_id_type,
      commission: orderData.commission,
      costPrice: orderData.costPrice,
      sellingPrice: orderData.sellingPrice,
      proof_of_payment: orderData.proof_of_payment,
      giftcardType: orderData.giftcardType,
      product_face_code: orderData.product_face_code,
      cryptoType: orderData.cryptoType,
      crypto_value: orderData.crypto_value,
      note1: orderData.note1,
      note2: orderData.note2,
      note3: orderData.note3,
      // Extract hotel details from notes or other fields
      check_in_date: orderData.check_in_date,
      check_out_date: orderData.check_out_date,
      total_nights: orderData.total_nights,
      total_adults: orderData.total_adults,
      total_children: orderData.total_children,
      special_requests: orderData.special_requests || orderData.note1,
      check_in_time: orderData.check_in_time || "14:00",
      check_out_time: orderData.check_out_time || "12:00",
      hotel_name: orderData.hotel_name || "Hotel",
      hotel_branch: orderData.hotel_branch,
      guest_id_type: orderData.guest_id_type,
      guest_id_number: orderData.guest_id_number,
      emergency_contact: orderData.emergency_contact
    };
  };

  // Calculate totals based on actual data structure
  const unpaidBookings = bookings.filter(
    (booking) => booking.payment_status === '0' || booking.payment_status === "2"
  );

  const selectedBookingTotal = unpaidBookings
    .filter((booking) => selectedBookings.includes(booking.order_id))
    .reduce((sum, booking) => {
      const balanceDue = parseFloat(booking.balance_due) || 0;
      return sum + balanceDue;
    }, 0);

  const totalOutstanding = unpaidBookings.reduce((sum, booking) => {
    const balanceDue = parseFloat(booking.balance_due) || 0;
    return sum + balanceDue;
  }, 0);

  const getPaymentStatusLabel = (status: string | number) => {
    const code = Number(status);
    switch (code) {
      case 0:
        return "Unpaid";
      case 1:
        return "Paid";
      case 2:
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error";
      case "1":
        return "success";
      case "2":
        return "warning";
      default:
        return "default";
    }
  };

  const getBookingStatusColor = (status: string) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case "CONFIRMED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      case "CHECKED_OUT":
        return "info";
      case "COMPLETED":
        return "success";
      default:
        return "default";
    }
  };

  const getBookingStatusIcon = (status: string) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case "CONFIRMED":
      case "COMPLETED":
        return <CheckCircle />;
      case "PENDING":
        return <Warning />;
      case "CANCELLED":
        return <Close />;
      case "CHECKED_OUT":
        return <ArrowUpward />;
      default:
        return <Warning />;
    }
  };

  const toggleRowExpansion = (bookingId: string) => {
    setExpandedRows((prev) =>
      prev.includes(bookingId)
        ? prev.filter((id) => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  // Search bookings by booking code, guest name, phone, or email
  const handleSearchChange = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setSearchedBookings([]);
      return;
    }
    
    try {
      setLoading((prev) => ({ ...prev, search: true }));
      
      // Use the existing search function - it will work with your API
      const response: SearchResponse = await searchTransactionsByCode(searchTerm);

      console.log("Search response for bookings:", response);
      
      if (response.status && response.data.orders) {
        // Transform API response to HotelBooking format
        const transformedBookings = response.data.orders.map(transformApiOrderToHotelBooking);
        setSearchedBookings(transformedBookings);
        setBalance(response.data.balance || 0);
      } else {
        setSearchedBookings([]);
      }
    } catch (error) {
      console.error("Error searching bookings:", error);
      setSearchedBookings([]);
    } finally {
      setLoading((prev) => ({ ...prev, search: false }));
    }
  };

  // Handle booking selection from search results
  const handleBookingSelect = (booking: HotelBooking) => {
    setSelectedBooking(booking);
    setBookings([booking]); // Set as single booking in the table
    setSearchQuery("");
    setSearchedBookings([]);
    setSelectedBookings([]);
    setPaymentAmount(0);
    setExpandedRows([]);
  };

  const handleBookingDeselect = () => {
    setSelectedBooking(null);
    setBookings([]);
    setSelectedBookings([]);
    setPaymentAmount(0);
    setExpandedRows([]);
    setBalance(0);
  };

  const handleBookingSelectCheckbox = (bookingId: string) => {
    setSelectedBookings((prev) => {
      const newSelected = prev.includes(bookingId)
        ? prev.filter((id) => id !== bookingId)
        : [...prev, bookingId];

      // Update payment amount to match selected bookings
      const newTotal = unpaidBookings
        .filter((booking) => newSelected.includes(booking.order_id))
        .reduce(
          (sum, booking) => sum + (parseFloat(booking.balance_due) || 0),
          0
        );

      setPaymentAmount(newTotal);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBookings([]);
      setPaymentAmount(0);
    } else {
      const allUnpaidIds = unpaidBookings.map((booking) => booking.order_id);
      setSelectedBookings(allUnpaidIds);
      setPaymentAmount(totalOutstanding);
    }
    setSelectAll(!selectAll);
  };

  const handlePaymentAmountChange = (value: number) => {
    setPaymentAmount(Math.abs(value) || 0);
  };

  const getPaymentType = () => {
    if (paymentAmount === selectedBookingTotal) return "exact";
    if (paymentAmount > selectedBookingTotal) return "overpayment";
    if (paymentAmount < selectedBookingTotal) return "partial";
    return "exact";
  };

  const handlePaymentSubmit = async () => {
    if (!selectedBooking || selectedBookings.length === 0 || paymentAmount <= 0) return;

    try {
      setLoading((prev) => ({ ...prev, payment: true }));
      const paymentType = getPaymentType();

      const data = {
        customer_id: selectedBooking.member_id,
        order_ids: selectedBookings,
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_type: paymentType,
        notes: paymentNotes || `Payment for booking ${selectedBooking.order_code}`,
      };

      const response = await createCustomerPayment(data);
      
      if (response.status !== true) {
        console.error("Payment creation failed:", response.message);
        return;
      }

      // Generate receipt with payment details
      handleGenerateReceipt(data);
      
      // Refresh the booking data
      if (searchQuery) {
        await handleSearchChange(searchQuery);
      }

      // Reset payment dialog
      setShowPaymentDialog(false);
      setSelectedBookings([]);
      setPaymentAmount(0);
      setPaymentNotes("");
      setSelectAll(false);
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchedBookings([]);
      return;
    }
    const timerId = setTimeout(() => {
      handleSearchChange(searchQuery);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchQuery]);

  useEffect(() => {
    setSelectAll(
      selectedBookings.length === unpaidBookings.length &&
        unpaidBookings.length > 0
    );
  }, [selectedBookings, unpaidBookings]);

  // Receipt functionality
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);

  const handleGenerateReceipt = (paymentData: any) => {
    if (!selectedBooking) return;

    const paidBookings = bookings.filter(booking => 
      paymentData.order_ids.includes(booking.order_id)
    );

    const receiptItems = paidBookings.flatMap(booking => 
      booking.items.map((item, index) => ({
        orderID: `Item ${index + 1}`,
        productName: item.product_name || 'Hotel Room',
        quantity: item.quantity || '1',
        description: item.room_type ? `${item.room_type} Room` : 'Hotel Accommodation',
        unitPrice: parseFloat(item.card_rate) || 0,
        amount: parseFloat(item.total_amount || booking.total_price) || 0,
        status: booking.order_status,
        // Hotel specific fields
        checkIn: item.check_in_date,
        checkOut: item.check_out_date,
        nights: item.total_nights,
        guests: `${item.adults_count || 1} Adult${item.adults_count > 1 ? 's' : ''}${item.children_count ? `, ${item.children_count} Children` : ''}`
      }))
    );

    const totalAmount = paidBookings.reduce((sum, booking) => {
      return sum + parseFloat(booking.total_price);
    }, 0);

    const paidAmount = paymentData.amount;
    const balanceDue = Math.max(0, totalAmount - paidAmount);

    setReceiptData({
      customer: {
        name: selectedBooking.customer_name || 'Guest',
        phone: selectedBooking.customer_phone || 'N/A',
        email: selectedBooking.customer_email || 'N/A'
      },
      order: {
        id: selectedBooking.order_code,
        date: selectedBooking.order_date,
        status: selectedBooking.order_status,
        paymentStatus: '1',
        paymentMethod: paymentData.payment_method || 'cash',
        hotelName: selectedBooking.hotel_name || "Hotel",
        branch: selectedBooking.hotel_branch
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue
      },
      payment: {
        amount: paidAmount,
        method: paymentData.payment_method || 'cash',
        date: new Date().toISOString(),
        notes: paymentData.notes || `Payment for booking ${selectedBooking.order_code}`
      }
    });
    setReceiptOpen(true);
  };

  const handlePrintReceipt = (booking: HotelBooking) => {
    const receiptItems = booking.items.map((item, index) => ({
      orderID: `Item ${index + 1}`,
      productName: item.product_name,
      quantity: item.quantity || "1",
      unitPrice: parseFloat(item.card_rate) || 0,
      amount: parseFloat(booking.total_price),
      description: item.room_type ? `${item.room_type} Room` : 'Hotel Accommodation',
      status: booking.order_status,
      checkIn: item.check_in_date,
      checkOut: item.check_out_date,
      nights: item.total_nights
    }));

    const totalAmount = parseFloat(booking.total_price);
    const paidAmount = parseFloat(booking.amount_paid);
    const balanceDue = parseFloat(booking.balance_due);

    setReceiptData({
      customer: {
        name: booking.customer_name || 'Guest',
        phone: booking.customer_phone || 'N/A',
        email: booking.customer_email || 'N/A'
      },
      order: {
        id: booking.order_code,
        date: booking.order_date,
        status: booking.order_status,
        paymentStatus: getPaymentStatusLabel(booking.payment_status),
        paymentMethod: booking.mode_of_payment || 'cash',
        hotelName: booking.hotel_name || "Hotel",
        branch: booking.hotel_branch
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue
      }
    });
    setReceiptOpen(true);
  };

  // Calculate totals for display
  const totalPayableAmount = bookings.reduce(
    (sum, booking) => sum + parseFloat(booking.payable_amount || booking.total_price || '0'),
    0
  );

  const totalBalanceDue = bookings.reduce(
    (sum, booking) => sum + parseFloat(booking.balance_due || '0'),
    0
  );

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return "Not specified";
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getRoomType = (booking: HotelBooking) => {
    if (booking.items[0]?.room_type) return booking.items[0].room_type;
    if (booking.product_type) return booking.product_type;
    return "Standard Room";
  };

  const getStayDuration = (booking: HotelBooking) => {
    const checkIn = booking.check_in_date || booking.items[0]?.check_in_date;
    const checkOut = booking.check_out_date || booking.items[0]?.check_out_date;
    const nights = booking.total_nights || booking.items[0]?.total_nights;
    
    if (checkIn && checkOut) {
      return `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
    } else if (nights) {
      return `${nights} night${nights > 1 ? 's' : ''}`;
    }
    return "Not specified";
  };

  return (
    <IonPage className="pushDown">
                <MenuButton/>
      
      <IonContent>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Search Hotel Bookings
          </Typography>

          {/* Booking Code Search */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Search by Booking Code, Guest Name, Phone, Email, or Room Number..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Hotel />
                  </InputAdornment>
                ),
                endAdornment: selectedBooking && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={handleBookingDeselect}
                    >
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={!!selectedBooking}
            />
            
            {loading.search && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {searchedBookings.length > 0 && (
              <Paper sx={{ mt: 1, maxHeight: '300px', overflow: "auto" }}>
                <List>
                  {searchedBookings.map((booking, index) => (
                    <ListItem
                      button
                      key={booking.order_id}
                      onClick={() => handleBookingSelect(booking)}
                    >
                      <ListItemText
                        primary={`Booking: ${booking.order_code}`}
                        secondary={
                          <Box>
                            <div>
                              <strong>Guest:</strong> {booking.customer_name}
                            </div>
                            <div>
                              <strong>Room:</strong> {booking.product_name} ({getRoomType(booking)})
                            </div>
                            <div>
                              <strong>Stay:</strong> {getStayDuration(booking)}
                            </div>
                            <div>
                              <strong>Amount:</strong> {formatCurrencyN(parseFloat(booking.total_price))}
                            </div>
                            <div>
                              <strong>Status:</strong> {getPaymentStatusLabel(booking.payment_status)} • {booking.order_status}
                            </div>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Booking Details Section */}
          {selectedBooking && (
            <Box>
              {/* Booking Info Header */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
                        <Hotel fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="h5">
                          Booking: {selectedBooking.order_code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Guest: {selectedBooking.customer_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Hotel: {selectedBooking.hotel_name || "Hotel"} {selectedBooking.hotel_branch && `- ${selectedBooking.hotel_branch}`}
                        </Typography>
                        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                          <Chip 
                            label={selectedBooking.order_status}
                            size="small"
                            color={getBookingStatusColor(selectedBooking.order_status) as any}
                            icon={getBookingStatusIcon(selectedBooking.order_status)}
                          />
                          <Chip 
                            label={getPaymentStatusLabel(selectedBooking.payment_status)}
                            size="small"
                            color={getPaymentStatusColor(selectedBooking.payment_status) as any}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <IconButton onClick={handleBookingDeselect}>
                      <Close />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              {/* Payment Summary */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: "info.light" }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Receipt sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Total Amount</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(parseFloat(selectedBooking.total_price))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    bgcolor: parseFloat(selectedBooking.balance_due) > 0 
                      ? "warning.light" 
                      : "success.light" 
                  }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Balance Due</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(parseFloat(selectedBooking.balance_due))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: "primary.light" }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <NightsStay sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Stay Details</Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {selectedBooking.total_nights || selectedBooking.items[0]?.total_nights || 1} night{(selectedBooking.total_nights || 1) > 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="body2">
                        {selectedBooking.total_adults || 1} Adult{(selectedBooking.total_adults || 1) > 1 ? 's' : ''}
                        {selectedBooking.total_children ? `, ${selectedBooking.total_children} Children` : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Guest and Stay Details */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Guest Information
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Person sx={{ mr: 1 }} />
                        <Typography variant="body1" fontWeight="bold">
                          {selectedBooking.customer_name}
                        </Typography>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Phone:</strong> {selectedBooking.customer_phone}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Email:</strong> {selectedBooking.customer_email}
                          </Typography>
                        </Grid>
                        {selectedBooking.guest_id_number && (
                          <>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                <strong>ID Type:</strong> {selectedBooking.guest_id_type}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">
                                <strong>ID Number:</strong> {selectedBooking.guest_id_number}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        {selectedBooking.emergency_contact && (
                          <Grid item xs={12}>
                            <Typography variant="body2">
                              <strong>Emergency Contact:</strong> {selectedBooking.emergency_contact}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Stay Details
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Box display="flex" alignItems="center">
                            <CalendarToday fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">
                              <strong>Check-in:</strong> {formatDate(selectedBooking.check_in_date || selectedBooking.items[0]?.check_in_date)} {selectedBooking.check_in_time || "14:00"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box display="flex" alignItems="center">
                            <CalendarToday fontSize="small" sx={{ mr: 0.5 }} />
                            <Typography variant="body2">
                              <strong>Check-out:</strong> {formatDate(selectedBooking.check_out_date || selectedBooking.items[0]?.check_out_date)} {selectedBooking.check_out_time || "12:00"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Room Type:</strong> {getRoomType(selectedBooking)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Room:</strong> {selectedBooking.product_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="primary">
                            <strong>Rate:</strong> {formatCurrencyN(parseFloat(selectedBooking.items[0]?.rate_per_night || selectedBooking.items[0]?.card_rate || "0"))} / night
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Special Requests */}
              {selectedBooking.special_requests && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Special Requests
                    </Typography>
                    <Typography variant="body2">
                      {selectedBooking.special_requests}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Payment Actions */}
              {(selectedBooking.payment_status === '0' || selectedBooking.payment_status === '2') && (
                <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Payment />}
                    onClick={() => {
                      setSelectedBookings([selectedBooking.order_id]);
                      setPaymentAmount(parseFloat(selectedBooking.balance_due));
                      setShowPaymentDialog(true);
                    }}
                  >
                    Record Payment
                  </Button>
                </Box>
              )}

              {/* Booking Details Table */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Booking Details
              </Typography>

              {loading.bookings ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : bookings.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Booking Code</TableCell>
                        <TableCell>Guest</TableCell>
                        <TableCell>Room</TableCell>
                        <TableCell align="center">Stay Period</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right">Balance Due</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Booking Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bookings.map((booking) => {
                        const isExpanded = expandedRows.includes(booking.order_id);
                        const mainItem = booking.items[0];

                        return (
                          <React.Fragment key={booking.order_id}>
                            <TableRow
                              sx={{
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {booking.order_code}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                    {booking.customer_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {booking.customer_phone}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                    {booking.product_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {getRoomType(booking)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography variant="body2">
                                    {getStayDuration(booking)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    icon={<NightsStay />}
                                    label={`${booking.total_nights || mainItem?.total_nights || 1} night${(booking.total_nights || 1) > 1 ? 's' : ''}`}
                                    color="info"
                                    variant="outlined"
                                    sx={{ mt: 0.5 }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: "bold" }}
                              >
                                {formatCurrencyN(parseFloat(booking.total_price))}
                              </TableCell>
                              <TableCell align="right" sx={{
                                color: parseFloat(booking.balance_due) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {formatCurrencyN(parseFloat(booking.balance_due))}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getPaymentStatusLabel(booking.payment_status)}
                                  size="small"
                                  color={getPaymentStatusColor(booking.payment_status) as any}
                                  icon={Number(booking.payment_status) === 1 ? <CheckCircle fontSize="small" /> : undefined}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={booking.order_status}
                                  size="small"
                                  color={getBookingStatusColor(booking.order_status) as any}
                                  icon={getBookingStatusIcon(booking.order_status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View booking details">
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(booking.order_id)}
                                  >
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                </Tooltip>
                                {Number(booking.payment_status) === 1 && (
                                  <Tooltip title="Print receipt">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handlePrintReceipt(booking)}
                                      color="primary"
                                      sx={{ ml: 1 }}
                                    >
                                      <Receipt fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>

                            {/* Expandable row for booking details */}
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={9}
                              >
                                <Collapse
                                  in={isExpanded}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ margin: 1, p: 2, bgcolor: 'background.default' }}>
                                    <Typography variant="h6" gutterBottom>
                                      Booking Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="body2">
                                          <strong>Guest Name:</strong> {booking.customer_name}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Email:</strong> {booking.customer_email}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Phone:</strong> {booking.customer_phone}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Payment Method:</strong> {booking.mode_of_payment}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Hotel:</strong> {booking.hotel_name || "Hotel"} {booking.hotel_branch && `- ${booking.hotel_branch}`}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Check-in Time:</strong> {booking.check_in_time || "14:00"}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Check-out Time:</strong> {booking.check_out_time || "12:00"}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="body2">
                                          <strong>Booking Date:</strong> {formatDate(booking.order_date)}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Booked By:</strong> {booking.order_by}
                                        </Typography>
                                        {booking.guest_id_number && (
                                          <>
                                            <Typography variant="body2">
                                              <strong>ID Type:</strong> {booking.guest_id_type}
                                            </Typography>
                                            <Typography variant="body2">
                                              <strong>ID Number:</strong> {booking.guest_id_number}
                                            </Typography>
                                          </>
                                        )}
                                        {booking.emergency_contact && (
                                          <Typography variant="body2">
                                            <strong>Emergency Contact:</strong> {booking.emergency_contact}
                                          </Typography>
                                        )}
                                      </Grid>
                                    </Grid>
                                    {booking.special_requests && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                          <strong>Special Requests:</strong> {booking.special_requests}
                                        </Typography>
                                      </Box>
                                    )}
                                    {booking.note1 && (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          <strong>Notes:</strong> {booking.note1}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No booking details available
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* Payment Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Record Payment for Booking {selectedBooking?.order_code}
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowPaymentDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Guest: {selectedBooking?.customer_name}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Booking Code: {selectedBooking?.order_code}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Room: {selectedBooking?.product_name} ({getRoomType(selectedBooking!)})
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Stay: {getStayDuration(selectedBooking!)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Total Amount Due:{" "}
                    <strong>{formatCurrencyN(selectedBookingTotal)}</strong>
                  </Typography>

                  {/* Payment Type Alert */}
                  {paymentAmount > 0 && (
                    <Alert
                      severity={
                        getPaymentType() === "exact"
                          ? "success"
                          : getPaymentType() === "overpayment"
                          ? "info"
                          : "warning"
                      }
                      sx={{ mt: 2 }}
                    >
                      {getPaymentType() === "exact" &&
                        "Exact payment - booking will be marked as paid"}
                      {getPaymentType() === "overpayment" &&
                        `Overpayment of ${formatCurrencyN(
                          paymentAmount - selectedBookingTotal
                        )} - excess will be added to account credit`}
                      {getPaymentType() === "partial" &&
                        `Partial payment - ${formatCurrencyN(
                          selectedBookingTotal - paymentAmount
                        )} will remain outstanding`}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) =>
                      handlePaymentAmountChange(parseFloat(e.target.value))
                    }
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₦</InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPaymentAmount(selectedBookingTotal)}
                    >
                      Exact Amount
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setPaymentAmount(selectedBookingTotal * 0.5)
                      }
                    >
                      50%
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setPaymentAmount(selectedBookingTotal * 0.25)
                      }
                    >
                      25%
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">POS</MenuItem>
                    <MenuItem value="transfer">Bank Transfer</MenuItem>
                    <MenuItem value="wallet">Wallet</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    label="Payment Notes"
                    multiline
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Optional payment notes..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handlePaymentSubmit}
                disabled={
                  !paymentAmount ||
                  selectedBookings.length === 0 ||
                  loading.payment
                }
                startIcon={
                  loading.payment ? <CircularProgress size={20} /> : <Receipt />
                }
              >
                Record Payment ({formatCurrencyN(paymentAmount)})
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>

      {/* Receipt Modal */}
      {getBusinessData() && getBusinessData().receipt_layout === 'large' && (
        receiptData && (
          <ReceiptModal_giftcard_crypto
            open={receiptOpen}
            onClose={() => setReceiptOpen(false)}
            receiptData={receiptData}
          />
        )
      )}
    </IonPage>
  );
};

export default HotelBookingSearch;